/** sessionStorage key for the track calls a document hands to the next one instead of sending them. */
const CARRIED_EVENTS_KEY = 'fi_carried_analytics_events';

export interface TrackCall {
  name: string;
  properties: any;
  options?: SegmentAnalytics.SegmentOpts & { timestamp?: string };
}

/**
 * Stores calls for the next document, stamped with this page and the time, which analytics.js would
 * otherwise read from the document that sends them. The url is the live one, as on page views.
 * Returns false when storage is unavailable, and the caller sends them here instead.
 */
export function storeCarriedEvents(calls: TrackCall[]) {
  const { href, pathname, search } = window.location;
  const campaign = Object.fromEntries(
    [...new URLSearchParams(search)]
      .filter(([param]) => param.startsWith('utm_'))
      .map(([param, value]) => [param === 'utm_campaign' ? 'name' : param.slice('utm_'.length), value]),
  );
  const context = {
    page: { path: pathname, referrer: document.referrer, search, title: document.title, url: href.split('#')[0] },
    ...(Object.keys(campaign).length > 0 && { campaign }),
  };
  const timestamp = new Date().toISOString();
  const stamped = calls.map(({ name, properties, options }) => {
    const callContext: { page?: object } | undefined = options?.context;
    return {
      name,
      properties,
      // a page field the call already carries (the hop referrer) wins over this page's, as in analytics.js
      options: {
        ...options,
        timestamp,
        context: { ...callContext, ...context, page: { ...context.page, ...callContext?.page } },
      },
    };
  });
  try {
    sessionStorage.setItem(CARRIED_EVENTS_KEY, JSON.stringify([...takeCarriedEvents(), ...stamped]));
    return true;
  } catch {
    return false;
  }
}

/** The calls an earlier document stored, read once. */
export function takeCarriedEvents(): TrackCall[] {
  try {
    const stored = sessionStorage.getItem(CARRIED_EVENTS_KEY);
    sessionStorage.removeItem(CARRIED_EVENTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}
